import React, { useRef } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import RichTextEditor from '../../../../../../../ui/RichTextEditor';
import defaultWellnessEmailTemplate from '../../../Config/Containers/InivitationTemplate/email_template.json';

interface InvitationTabProps {
  emailTemplate: string;
  onChange: (updated: string) => void;
  onSave: () => void;
  isSaving: boolean;
}

const MERGE_TAGS: { name: string; variable: string }[] = [
  { name: 'Applicant Name', variable: 'applicant_name' },
  { name: 'Company Name', variable: 'company_name' },
  { name: 'Job Title', variable: 'job_title_name' },
  { name: 'End of Assessment', variable: 'end_of_assessment' },
  { name: 'Support Email', variable: 'support_email' },
  { name: 'Wellness Screening Link', variable: 'wellness_url' },
];

const DEFAULT_JSON_TEMPLATE = JSON.stringify(defaultWellnessEmailTemplate);

export const InvitationTab: React.FC<InvitationTabProps> = ({
  emailTemplate,
  onChange,
  onSave,
  isSaving,
}) => {
  const editorRef = useRef<any>(null);

  const handleEditorApply = (html: string) => {
    onChange(html);
  };

  const handleEditorReady = (editor: any) => {
    editorRef.current = editor;
  };

  const handleTagClick = (variable: string) => {
    editorRef.current
      ?.chain()
      .focus()
      .insertContent(`{{${variable}}}`)
      .run();
  };

  const handleSave = () => {
    const latestBody = editorRef.current
      ? editorRef.current.getHTML()
      : emailTemplate;
    onChange(latestBody);
    onSave();
  };

  const handleCancel = () => {
    if (editorRef.current && emailTemplate) {
      editorRef.current.commands.setContent(emailTemplate);
    }
  };

  const isValid = emailTemplate.trim() !== '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Heading */}
      <Typography variant='subtitle1' sx={{ fontWeight: 800, color: 'text.primary' }}>
        Wellness Invitation Template
      </Typography>

      {/* Body */}
      <Box>
        <Typography
          variant='caption'
          sx={{
            display: 'block',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'text.secondary',
            mb: 1,
          }}
        >
          Body
        </Typography>
        <RichTextEditor
          key='wellness-invitation'
          jsonStringTemplate={DEFAULT_JSON_TEMPLATE}
          value={emailTemplate || undefined}
          validVariables={MERGE_TAGS}
          saveTemplate={handleEditorApply}
          onEditorReady={handleEditorReady}
          buttonText='Apply Body'
        />
      </Box>

      {/* Merge-tag chips below the editor */}
      <Box>
        <Typography
          variant='caption'
          sx={{
            display: 'block',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'text.secondary',
            mb: 1,
          }}
        >
          Quick-insert merge tags
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {MERGE_TAGS.map((tag) => (
            <Chip
              key={tag.variable}
              label={`{{${tag.variable}}}`}
              size='small'
              onClick={() => handleTagClick(tag.variable)}
              clickable
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                bgcolor: 'grey.100',
                '&:hover': {
                  bgcolor: 'primary.light',
                  color: 'primary.contrastText',
                },
              }}
            />
          ))}
        </Box>
        <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
          Clicks insert the tag at the current cursor position in the editor
        </Typography>
      </Box>

      <Divider />

      {/* Action row */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
        <Button
          variant='outlined'
          color='inherit'
          onClick={handleCancel}
          disabled={isSaving}
          sx={{ borderRadius: 2, textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleSave}
          disabled={!isValid || isSaving}
          startIcon={isSaving ? <CircularProgress size={16} color='inherit' /> : null}
          sx={{ borderRadius: 2, textTransform: 'none' }}
        >
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </Box>
    </Box>
  );
};